import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Alert, FormControl, InputLabel, Select, MenuItem, Chip } from '@mui/material';
import { fetchJson } from '../../utils/fetchJson';
import ChildPicker from '../../components/Parent/ChildPicker';

export default function ParentAttendancePage() {
  const { t } = useTranslation();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState('');
  const [records, setRecords] = useState([]);
  const [subjectFilter, setSubjectFilter] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchJson('/api/portal/parent/children')
      .then((data) => {
        if (!alive) return;
        const rows = Array.isArray(data?.children) ? data.children : [];
        setChildren(rows);
        if (rows.length > 0) setSelectedChild(rows[0].studentId || rows[0].id);
      })
      .catch((e) => { if (alive) setError(e?.message || t('parentAttendance.errors.loadChildren')); });
    return () => { alive = false; };
  }, [t]);

  useEffect(() => {
    let alive = true;
    if (!selectedChild) return () => { alive = false; };
    setError(null);
    setSubjectFilter('');
    fetchJson(`/api/portal/parent/child/${encodeURIComponent(selectedChild)}/attendance`)
      .then((data) => {
        if (!alive) return;
        setRecords(Array.isArray(data?.records) ? data.records : []);
      })
      .catch((e) => { if (alive) setError(e?.message || t('parentAttendance.errors.loadAttendance')); });
    return () => { alive = false; };
  }, [selectedChild, t]);

  const selectedChildData = children.find(c => String(c.studentId || c.id) === String(selectedChild));
  const childName = selectedChildData ? `${selectedChildData.firstName || ''} ${selectedChildData.lastName || ''}`.trim() : '';
  const subjectOptions = Array.from(
    new Map(
      (records || [])
        .filter(r => r.subjectId != null)
        .map(r => [String(r.subjectId), { id: r.subjectId, name: r.subjectName || r.subjectCode || t('parentAttendance.subjectFallback', { id: r.subjectId }) }])
    ).values()
  );
  subjectOptions.sort((a, b) => String(a.name).localeCompare(String(b.name)));

  const visibleRecords = (records || []).filter(r => {
    if (!subjectFilter) return true;
    return String(r.subjectId) === String(subjectFilter);
  });

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" gutterBottom>{t('parentAttendance.title')}</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <ChildPicker
        children={children}
        value={selectedChild}
        onChange={setSelectedChild}
        label={t('parentAttendance.selectChild')}
        studentWord={t('parentAttendance.student')}
        sx={{ mb: 2 }}
      />

      {children.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 260, flex: 1 }}>
            <InputLabel>{t('parentAttendance.subject')}</InputLabel>
            <Select value={subjectFilter} label={t('parentAttendance.subject')} onChange={(e) => setSubjectFilter(e.target.value)}>
              <MenuItem value="">{t('parentAttendance.allSubjects')}</MenuItem>
              {subjectOptions.map(s => (
                <MenuItem key={String(s.id)} value={String(s.id)}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {selectedChild && childName && (
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
          {childName}
        </Typography>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('parentAttendance.columns.date')}</TableCell>
            <TableCell>{t('parentAttendance.columns.subject')}</TableCell>
            <TableCell>{t('parentAttendance.columns.status')}</TableCell>
            <TableCell>{t('parentAttendance.columns.remarks')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRecords.map(r => (
            <TableRow key={r.id}>
              <TableCell>{r.date ? new Date(r.date).toLocaleDateString() : ''}</TableCell>
              <TableCell>{r.subjectName || r.subjectCode || (r.subjectId != null ? t('parentAttendance.subjectFallback', { id: r.subjectId }) : '')}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={t(`parentAttendance.statuses.${String(r.status || '').toLowerCase()}`, String(r.status || ''))}
                  color={String(r.status || '').toLowerCase() === 'absent' ? 'error' : (String(r.status || '').toLowerCase() === 'late' ? 'warning' : 'default')}
                />
              </TableCell>
              <TableCell>{r.remarks || ''}</TableCell>
            </TableRow>
          ))}
          {visibleRecords.length === 0 && (
            <TableRow>
              <TableCell colSpan={4}>{t('parentAttendance.empty')}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
